import { Col, Empty, Modal, Row, Spin, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firestore } from '../../firebase/firebase';
import { FirestoreCollection } from '../../firebase/firebase.models';
import { WantedListing } from '../../models/wantedListings.model';
import { calculateDistance } from '../../utils/calculateDistance';
import { useAuth } from '../Auth/AuthContext';

import { EditWantedListingModal } from './EditWantedListingModal';
import { useWantedListings } from './hooks/useWantedListings';
import { LocationFilter } from './LocationFilter';
import { SubmitOfferModal } from './SubmitOfferModal';
import { WantedListingCard } from './WantedListingCard';

export const WantedListingsWall: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation('wanted-listings');
  const { listings, loading, error } = useWantedListings();
  const [selectedListing, setSelectedListing] = useState<WantedListing | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingListing, setEditingListing] = useState<WantedListing | null>(null);
  const [userNames, setUserNames] = useState<{ [uid: string]: string }>({});

  // Stan filtrów lokalizacji
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [filterCoordinates, setFilterCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [filterRadius, setFilterRadius] = useState<number>(10);

  useEffect(() => {
    const fetchUserNames = async () => {
      const names: { [uid: string]: string } = {};
      const uniqueUids = Array.from(new Set(listings.map((l) => l.uid).filter(Boolean)));

      const batchSize = 10;
      for (let i = 0; i < uniqueUids.length; i += batchSize) {
        const batch = uniqueUids.slice(i, i + batchSize);
        const promises = batch.map(async (uid) => {
          try {
            const userDoc = await firestore.collection(FirestoreCollection.USERS).doc(uid).get();
            const userData = userDoc.data();
            names[uid] = userData?.firstName
              ? `${userData.firstName} ${userData.lastName || ''}`.trim()
              : userData?.email ?? '';
          } catch {
            names[uid] = '';
          }
        });
        await Promise.allSettled(promises);
      }

      setUserNames(names);
    };

    if (listings.length > 0) {
      fetchUserNames();
    }
  }, [listings]);

  const handleOfferClick = (listing: WantedListing) => {
    setSelectedListing(listing);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedListing(null);
  };

  const handleEdit = (listing: WantedListing) => {
    setEditingListing(listing);
    setIsEditModalVisible(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalVisible(false);
    setEditingListing(null);
  };

  const handleDelete = async (listing: WantedListing) => {
    Modal.confirm({
      title: t('WALL.DELETE_CONFIRM_TITLE'),
      content: t('WALL.DELETE_CONFIRM_CONTENT'),
      okText: t('WALL.DELETE_BUTTON'),
      cancelText: t('WALL.CANCEL_BUTTON'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await firestore.collection('wantedListings').doc(listing.id).update({
            status: 'deleted',
          });
          message.success(t('MESSAGES.DELETE_SUCCESS'));
        } catch (error) {
          console.error('Error deleting listing:', error);
          message.error(t('MESSAGES.DELETE_ERROR'));
        }
      },
    });
  };

  // Filtruj listingi po lokalizacji
  const filteredListings = useMemo(() => {
    if (!filterCoordinates) {
      return listings;
    }

    return listings.filter((listing) => {
      // Jeśli listing nie ma współrzędnych, nie możemy go filtrować
      if (!listing.locationCoordinates) {
        return false;
      }

      const distance = calculateDistance(
        filterCoordinates.lat,
        filterCoordinates.lng,
        listing.locationCoordinates.lat,
        listing.locationCoordinates.lng
      );

      return distance <= filterRadius;
    });
  }, [listings, filterCoordinates, filterRadius]);

  const handleLocationFilterChange = (location: string, coordinates?: { lat: number; lng: number }) => {
    setFilterLocation(location);
    setFilterCoordinates(coordinates || null);
  };

  const handleClearFilters = () => {
    setFilterLocation('');
    setFilterCoordinates(null);
    setFilterRadius(10);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <Empty description={t('WALL.LOADING_ERROR', { error })} className="my-12" />;
  }

  if (!loading && listings.length === 0) {
    return <Empty description={t('WALL.NO_LISTINGS')} className="my-12" />;
  }

  const showEmptyFilteredState = filterCoordinates && filteredListings.length === 0;

  return (
    <>
      <LocationFilter
        filterLocation={filterLocation}
        filterRadius={filterRadius}
        onLocationChange={handleLocationFilterChange}
        onRadiusChange={setFilterRadius}
        onClear={handleClearFilters}
      />

      {showEmptyFilteredState ? (
        <Empty
          description={`Brak ogłoszeń w promieniu ${filterRadius} km od lokalizacji ${filterLocation}`}
          className="my-12"
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filteredListings.map((listing) => {
            const isOwner = currentUser?.uid === listing.uid;

            return (
              <Col key={listing.id} xs={24} md={12} lg={8}>
                <WantedListingCard
                  listing={listing}
                  companyName={userNames[listing.uid]}
                  onOfferClick={() => handleOfferClick(listing)}
                  onEdit={() => handleEdit(listing)}
                  onDelete={() => handleDelete(listing)}
                  isOwner={isOwner}
                />
              </Col>
            );
          })}
        </Row>
      )}

      <SubmitOfferModal visible={isModalVisible} onClose={handleModalClose} listing={selectedListing} />
      {isEditModalVisible && editingListing && (
        <EditWantedListingModal visible={isEditModalVisible} onClose={handleEditModalClose} listing={editingListing} />
      )}
    </>
  );
};
